// @ts-nocheck
import { useState, useMemo } from "react";
import type { ReactNode } from "react";
import {
  Button, Modal, Form, Input, InputNumber, Select, Switch, Typography,
  message, Dropdown,
} from "../components/admin-ui";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ApiOutlined, CarOutlined,
  MoreOutlined, CheckCircleOutlined, CloseCircleOutlined,
} from "../components/admin-icons";
import { useTranslation } from "react-i18next";
import type { ColumnsType } from "../components/admin-ui";
import type { DeliveryProvider } from "@kawkaw/shared";
import { DataTable } from "../components/DataTable";
import { tokens } from "../styles/tokens";
import { AdminShell } from "../layouts/AdminShell";
import {
  useDeliveryProviders, useCreateDeliveryProvider,
  useUpdateDeliveryProvider, useDeleteDeliveryProvider, useTestDeliveryProvider,
} from "../api/hooks";

const { Text } = Typography;
const { color: T } = tokens;

function DeliveryStatCard({ label, value, icon, iconColor, badge, badgeColor, badgeBg }: {
  label: string;
  value: number;
  icon: ReactNode;
  iconColor: string;
  badge: string;
  badgeColor: string;
  badgeBg: string;
}) {
  return (
    <div className="admin-kpi-card admin-delivery-provider-stat">
      <div className="admin-kpi-heading flex">
        <div className="admin-kpi-label">{label}</div>
        <span className="admin-kpi-icon" style={{ color: iconColor, background: badgeBg }}>{icon}</span>
      </div>
      <div className="admin-kpi-value">{value.toLocaleString()}</div>
      <div className="admin-provider-stat-note" style={{ color: badgeColor }}>{badge}</div>
    </div>
  );
}

const PROVIDER_ICONS: Record<string, string> = {
  lalamove: "🚚", grabexpress: "🚗", foodpanda: "🐼",
  pandago: "🐼", myposrider: "🛵", manual: "👤", custom: "⚙️",
};

// Known base URLs per provider — used to auto-fill base_url when Sandbox Mode
// is toggled, so an admin never has to hand-type (or mistype) a provider's
// API host. Only providers with well-known REST endpoints are listed; others
// fall back to manual entry.
const PROVIDER_URLS: Record<string, { sandbox: string; production: string }> = {
  lalamove: { sandbox: "https://rest.sandbox.lalamove.com", production: "https://rest.lalamove.com" },
};

export function DeliveryProvidersScreen() {
  const { t } = useTranslation();
  const providerOptions = useMemo(
    () =>
      (
        [
          ["lalamove", "providerLalamove"],
          ["grabexpress", "providerGrabexpress"],
          ["foodpanda", "providerFoodpanda"],
          ["pandago", "providerPandago"],
          ["myposrider", "providerMyposrider"],
          ["manual", "providerManual"],
          ["custom", "providerCustom"],
        ] as const
      ).map(([value, key]) => ({ value, label: t(`deliveryProviders.${key}`) })),
    [t],
  );
  const providerLabel = (v: string) => providerOptions.find(p => p.value === v)?.label ?? v;
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<DeliveryProvider | null>(null);
  const [form] = Form.useForm();

  // Auto-fill base_url from the known sandbox/production pair whenever the
  // provider or the Sandbox Mode switch changes — an admin should never have
  // to hand-type (or mistype) a provider's API host.
  const applyKnownUrl = (provider: string | undefined, isSandbox: boolean) => {
    const known = provider ? PROVIDER_URLS[provider] : undefined;
    if (known) form.setFieldValue("base_url", isSandbox ? known.sandbox : known.production);
  };

  const { data, isLoading } = useDeliveryProviders();
  const createProvider = useCreateDeliveryProvider();
  const updateProvider = useUpdateDeliveryProvider();
  const deleteProvider = useDeleteDeliveryProvider();
  const testProvider = useTestDeliveryProvider();

  const openCreate = () => { setEditing(null); form.resetFields(); setModalOpen(true); };
  const openEdit = (row: DeliveryProvider) => {
    setEditing(row);
    form.setFieldsValue({
      ...row,
      coverage_km: row.coverage_km ? Number(row.coverage_km) : null,
    });
    setModalOpen(true);
  };

  const onSave = async (values: Record<string, unknown>) => {
    try {
      if (editing) {
        await updateProvider.mutateAsync({ id: editing.id, ...values });
        void message.success(t("deliveryProviders.updateSuccess"));
      } else {
        await createProvider.mutateAsync(values);
        void message.success(t("deliveryProviders.createSuccess"));
      }
      setModalOpen(false);
    } catch { void message.error(t("deliveryProviders.saveFailed")); }
  };

  const onDelete = async (id: string) => {
    try { await deleteProvider.mutateAsync(id); void message.success(t("deliveryProviders.deleteSuccess")); }
    catch { void message.error(t("deliveryProviders.deleteFailed")); }
  };

  const confirmDelete = (row: DeliveryProvider) => {
    Modal.confirm({
      title: t("deliveryProviders.confirmDelete"),
      okText: t("common.yes"),
      cancelText: t("common.no"),
      danger: true,
      onOk: () => onDelete(row.id),
    });
  };

  const onTest = async (id: string) => {
    try {
      const res = await testProvider.mutateAsync(id);
      const result = (res as { data?: { success: boolean; message: string; latency_ms?: number } }).data;
      if (result?.success) {
        void message.success(
          result.latency_ms != null
            ? t("common.connectionTestOkWithLatency", { message: result.message, ms: result.latency_ms })
            : t("common.connectionTestOk", { message: result.message }),
        );
      } else {
        void message.error(
          t("common.connectionTestFailed", {
            message: result?.message ?? t("transactions.testFailedGeneric"),
          }),
        );
      }
    } catch { void message.error(t("deliveryProviders.testFailed")); }
  };

  const items = (data as { data?: DeliveryProvider[] } | undefined)?.data ?? [];
  const activeCount = items.filter(item => item.is_active).length;
  const inactiveCount = items.length - activeCount;
  const sandboxCount = items.filter(item => item.is_sandbox).length;

  const columns: ColumnsType<DeliveryProvider> = [
    {
      title: t("deliveryProviders.colProvider"), dataIndex: "provider", key: "provider", width: 160,
      render: (v: string) => (
        <span className="admin-data-badge admin-delivery-provider-badge">
          {PROVIDER_ICONS[v] ?? "📦"} {providerLabel(v)}
        </span>
      ),
    },
    {
      title: t("deliveryProviders.colDisplayName"), dataIndex: "display_name", key: "display_name",
      render: (v: string) => <span className="admin-data-customer">{v.replace(/Lalammove/gi, "Lalamove")}</span>,
    },
    {
      title: t("deliveryProviders.colApiKey"), dataIndex: "api_key", key: "api_key", width: 160,
      render: (v: string | null) => v ? <Text className="admin-data-code">{v}</Text> : <span className="admin-data-secondary">—</span>,
    },
    {
      title: t("deliveryProviders.colBaseUrl"), dataIndex: "base_url", key: "base_url", width: 200,
      render: (v: string | null) => v ? <span className="admin-delivery-provider-url">{v}</span> : <span className="admin-data-secondary">—</span>,
    },
    {
      title: t("deliveryProviders.formSandboxMode", "Sandbox Mode"), dataIndex: "is_sandbox", key: "is_sandbox", width: 110,
      render: (v: boolean) => (
        <span className="admin-data-badge" style={{ color: v ? T.brandAmberText : T.successText, background: v ? T.warningBg : T.successBg }}>
          {v ? t("deliveryProviders.sandbox", "Sandbox") : t("deliveryProviders.production", "Production")}
        </span>
      ),
    },
    {
      title: t("deliveryProviders.colCoverage"), dataIndex: "coverage_km", key: "coverage_km", width: 110,
      render: (v: string | null) => v ? (
        <span className="admin-data-amount">{t("deliveryProviders.coverageKm", { km: Number(v).toFixed(0) })}</span>
      ) : <span className="admin-data-secondary">—</span>,
    },
    {
      title: t("deliveryProviders.colStatus"), dataIndex: "is_active", key: "active", width: 90,
      render: (v: boolean) => (
        <span className="admin-data-badge" style={{ color: v ? T.successText : T.textSecondary, background: v ? T.successBg : T.surfaceSection }}>
          {v ? t("common.yes") : t("common.no")}
        </span>
      ),
    },
    {
      title: t("deliveryProviders.colActions"), key: "actions", width: 96, fixed: "right" as const,
      render: (_: unknown, row: DeliveryProvider) => (
        <Dropdown
          menu={{ items: [
            { key: "test", icon: <ApiOutlined />, label: t("deliveryProviders.testConnection"), onClick: () => void onTest(row.id) },
            { key: "edit", icon: <EditOutlined />, label: t("common.edit"), onClick: () => openEdit(row) },
            { key: "delete", icon: <DeleteOutlined />, label: t("common.delete"), danger: true, onClick: () => confirmDelete(row) },
          ] }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button className="admin-delivery-provider-more admin-data-more" type="text" loading={testProvider.isPending}
            aria-label={`${t("deliveryProviders.colActions")} ${row.display_name}`} icon={<MoreOutlined style={{ fontSize: 18 }} />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <AdminShell>
      <div className="admin-delivery-providers-page admin-list-page">
        <div className="admin-provider-page-inner">
          <div className="admin-provider-toolbar">
            <div>
              <h1 className="admin-page-title">{t("deliveryProviders.title")}</h1>
              <div className="admin-provider-subtitle">{t("deliveryProviders.subtitle")}</div>
            </div>
            <Button className="admin-provider-create" type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              {t("deliveryProviders.addProvider")}
            </Button>
          </div>

          <section className="admin-delivery-providers-stats admin-txn-stats grid">
            <DeliveryStatCard label={t("deliveryProviders.statTotal")} value={items.length} icon={<CarOutlined />} iconColor={T.brand} badge={t("deliveryProviders.statBadgeOverall")} badgeColor={T.brand} badgeBg={T.brandLight} />
            <DeliveryStatCard label={t("deliveryProviders.statActive")} value={activeCount} icon={<CheckCircleOutlined />} iconColor={T.successText} badge={t("deliveryProviders.statBadgeActive")} badgeColor={T.successText} badgeBg={T.successBg} />
            <DeliveryStatCard label={t("deliveryProviders.statInactive")} value={inactiveCount} icon={<CloseCircleOutlined />} iconColor={T.textSecondary} badge={t("deliveryProviders.statBadgeInactive")} badgeColor={T.textSecondary} badgeBg={T.surfaceSection} />
            <DeliveryStatCard label={t("deliveryProviders.statSandbox")} value={sandboxCount} icon={<ApiOutlined />} iconColor={T.brandAmberText} badge={t("deliveryProviders.statBadgeSandbox")} badgeColor={T.brandAmberText} badgeBg={T.warningBg} />
          </section>

          <div className="admin-provider-results">
            {t("common.total", { count: items.length })}
          </div>

          <div className="admin-delivery-providers-table-panel admin-data-table-panel">
            <DataTable<DeliveryProvider>
              columns={columns}
              dataSource={items}
              rowKey="id"
              loading={isLoading}
              pagination={{ pageSize: 10 }}
            />
          </div>
        </div>
      </div>

      <Modal
        open={modalOpen}
        title={
          <span className="admin-provider-dialog-title">
            <CarOutlined />
            {editing ? t("deliveryProviders.editTitle") : t("deliveryProviders.newTitle")}
          </span>
        }
        onCancel={() => setModalOpen(false)}
        onOk={() => void form.submit()}
        okText={t("common.save")}
        okButtonProps={{ style: { background: T.brandAmber, borderColor: T.brandAmber }, loading: createProvider.isPending || updateProvider.isPending }}
        width={600}
        maskClosable={false}
      >
        <Form className="admin-delivery-provider-form" form={form} layout="vertical" colon={false} onFinish={onSave}>
          <div className="admin-provider-section-label">{t("deliveryProviders.sectionGeneral")}</div>
          <div className="admin-provider-form-fields">
            <Form.Item name="provider" label={t("deliveryProviders.formProvider")} rules={[{ required: true }]}>
              <Select options={providerOptions} placeholder={t("deliveryProviders.phSelectProvider")}
                onChange={(v: string) => applyKnownUrl(v, form.getFieldValue("is_sandbox") ?? true)} />
            </Form.Item>
            <Form.Item name="display_name" label={t("deliveryProviders.formDisplayName")} rules={[{ required: true }]}>
              <Input placeholder={t("deliveryProviders.phDisplayName")} />
            </Form.Item>
          </div>
          <Form.Item className="admin-provider-switch-card" name="is_sandbox" label={t("deliveryProviders.formSandboxMode", "Sandbox Mode")} valuePropName="checked" initialValue={true}
            extra={t("deliveryProviders.sandboxModeHint", "Sandbox uses the provider's test environment — switch off only once you have real production credentials.")}>
            <Switch checkedChildren={t("deliveryProviders.sandbox", "Sandbox")} unCheckedChildren={t("deliveryProviders.production", "Production")}
              onChange={(checked: boolean) => applyKnownUrl(form.getFieldValue("provider"), checked)} />
          </Form.Item>
          <div className="admin-provider-section-label">{t("deliveryProviders.sectionCredentials")}</div>
          <Form.Item name="api_key" label={t("deliveryProviders.formApiKey")}>
            <Input.Password placeholder={t("deliveryProviders.formApiKeyPlaceholder")} visibilityToggle />
          </Form.Item>
          <Form.Item name="api_secret" label={t("deliveryProviders.formApiSecret")}>
            <Input.Password placeholder={t("deliveryProviders.formApiSecretPlaceholder")} visibilityToggle />
          </Form.Item>
          <Form.Item name="webhook_secret" label={t("deliveryProviders.formWebhookSecret")}>
            <Input.Password placeholder={t("deliveryProviders.formWebhookSecretPlaceholder")} visibilityToggle />
          </Form.Item>
          <Form.Item name="base_url" label={t("deliveryProviders.formBaseUrl")}>
            <Input placeholder={t("deliveryProviders.phBaseUrl")} />
          </Form.Item>
          <div className="admin-provider-section-label">{t("deliveryProviders.sectionOperations")}</div>
          <div className="admin-provider-form-fields">
            <Form.Item name="coverage_km" label={t("deliveryProviders.formCoverageKm")}>
              <InputNumber min={0} max={200} precision={0} addonAfter={t("deliveryProviders.unitKm")} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="sort_order" label={t("deliveryProviders.formSortOrder")} initialValue={0}>
              <Input type="number" min={0} />
            </Form.Item>
          </div>
          <Form.Item className="admin-provider-switch-card" name="is_active" label={t("deliveryProviders.formIsActive")} valuePropName="checked" initialValue={true}>
            <Switch checkedChildren={t("common.yes")} unCheckedChildren={t("common.no")} />
          </Form.Item>
        </Form>
      </Modal>
    </AdminShell>
  );
}
